import { useState, useEffect } from 'react'
import { Recycle, Package, Plus, Trash, FloppyDisk, CheckCircle, Warning, DownloadSimple, FileCsv, FilePdf } from '@phosphor-icons/react'
import { supabase } from '../../lib/supabase'
import { downloadCSV, openPrintWindow } from '../esg/exportHelpers'

// ─── Style constants ───────────────────────────────────────────────────────────
const CARD = {
  background: '#FFFFFF',
  borderRadius: '8px',
  padding: '1.5rem',
  boxShadow: '0 1px 4px rgba(59,47,47,0.07)',
  border: '1px solid #DDD5C8',
}
const SECTION_GAP = { display: 'flex', flexDirection: 'column', gap: '1rem' }

// ─── Scoring tables ────────────────────────────────────────────────────────────
const ORIGIN_SCORES = { primario: 0, 'bio-renovable': 0.5, reciclado: 1.0, reutilizado: 1.0 }
const DETACH_SCORES = { A: 1.0, B: 0.75, C: 0.5, D: 0.25, E: 0 }
const HML = { alto: 1.0, medio: 0.5, bajo: 0 }  // high / mid / low

// ─── CI calculation ────────────────────────────────────────────────────────────
function calcCI(rows, cdw, pli) {
  const valid = rows.filter(r => parseFloat(r.masa_t) > 0)
  if (!valid.length) return null
  const total = valid.reduce((s, r) => s + parseFloat(r.masa_t), 0)
  let MOI = 0, DI = 0, MSI = 0, RPI = 0, MCI = 0
  valid.forEach(r => {
    const w = parseFloat(r.masa_t) / total
    MOI += w * (ORIGIN_SCORES[r.origen] ?? 0)
    DI  += w * (DETACH_SCORES[r.desmontabilidad] ?? 0)
    MSI += w * (HML[r.separabilidad] ?? 0)
    RPI += w * (HML[r.potencial_reciclaje] ?? 0)
    MCI += w * (HML[r.compatibilidad] ?? 0)
  })
  const cdwS = cdw / 100
  const pliS = pli / 100
  // Pre-use: origin 55%, pollutant inverse 20%, compatibility 10%, CDW 5%, neutral 10%
  const ci_c = 0.5 * (0.55 * MOI + 0.20 * (1 - pliS) + 0.10 * MCI + 0.05 * cdwS + 0.10 * 0.5)
  // Post-use: recycling potential 60%, detachability 20%, separability 20%
  const ci_f = 0.5 * (0.60 * RPI + 0.20 * DI + 0.20 * MSI)
  return {
    ci:   Math.min(1, Math.max(0, ci_c + ci_f)),
    ci_c: Math.min(0.5, Math.max(0, ci_c)),
    ci_f: Math.min(0.5, Math.max(0, ci_f)),
    MOI, DI, MSI, RPI, MCI, CDW: cdwS, PLI: pliS, total,
  }
}

function ciLevel(ci) {
  const p = ci * 100
  if (p >= 75) return { label: 'Excelente', color: '#5A8A5A' }
  if (p >= 60) return { label: 'Bueno',     color: '#8D9A2E' }
  if (p >= 45) return { label: 'Básico',    color: '#C98A2E' }
  return              { label: 'Bajo',      color: '#D4614A' }
}

// ─── New row factory ───────────────────────────────────────────────────────────
const newRow = (overrides = {}) => ({
  id: Date.now() + Math.random(),
  nombre: '', masa_t: '',
  origen: 'primario', compatibilidad: 'medio',
  desmontabilidad: 'C', separabilidad: 'medio', potencial_reciclaje: 'medio',
  ...overrides,
})

const PRESETS = [
  { nombre: 'Hormigón estructural',  masa_t: '', origen: 'primario',      compatibilidad: 'alto', desmontabilidad: 'E', separabilidad: 'bajo',  potencial_reciclaje: 'medio' },
  { nombre: 'Acero estructural',      masa_t: '', origen: 'primario',      compatibilidad: 'alto', desmontabilidad: 'B', separabilidad: 'alto',  potencial_reciclaje: 'alto'  },
  { nombre: 'Madera estructural',     masa_t: '', origen: 'bio-renovable', compatibilidad: 'alto', desmontabilidad: 'B', separabilidad: 'alto',  potencial_reciclaje: 'alto'  },
  { nombre: 'Cerramiento cerámico',   masa_t: '', origen: 'primario',      compatibilidad: 'medio',desmontabilidad: 'D', separabilidad: 'medio', potencial_reciclaje: 'bajo'  },
  { nombre: 'Vidrio',                 masa_t: '', origen: 'primario',      compatibilidad: 'alto', desmontabilidad: 'B', separabilidad: 'alto',  potencial_reciclaje: 'alto'  },
  { nombre: 'Aislamiento EPS/XPS',    masa_t: '', origen: 'primario',      compatibilidad: 'bajo', desmontabilidad: 'E', separabilidad: 'bajo',  potencial_reciclaje: 'bajo'  },
  { nombre: 'Aislamiento mineral',    masa_t: '', origen: 'primario',      compatibilidad: 'medio',desmontabilidad: 'C', separabilidad: 'medio', potencial_reciclaje: 'medio' },
  { nombre: 'Aluminio (carpintería)', masa_t: '', origen: 'primario',      compatibilidad: 'alto', desmontabilidad: 'A', separabilidad: 'alto',  potencial_reciclaje: 'alto'  },
  { nombre: 'Yeso / Escayola',        masa_t: '', origen: 'primario',      compatibilidad: 'medio',desmontabilidad: 'D', separabilidad: 'bajo',  potencial_reciclaje: 'bajo'  },
  { nombre: 'Plásticos',              masa_t: '', origen: 'primario',      compatibilidad: 'bajo', desmontabilidad: 'C', separabilidad: 'medio', potencial_reciclaje: 'medio' },
]

// ─── Sub-components ────────────────────────────────────────────────────────────
function SubBar({ label, value, color }) {
  return (
    <div style={{ marginBottom: '0.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2rem' }}>
        <span style={{ fontSize: '0.8rem', color: '#7A6A5A' }}>{label}</span>
        <span style={{ fontSize: '0.8rem', fontWeight: 600, color }}>{(value * 100).toFixed(0)}%</span>
      </div>
      <div style={{ height: 8, background: '#EDE7DE', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${value * 100}%`, background: color, borderRadius: 4, transition: 'width 0.3s' }} />
      </div>
    </div>
  )
}

// ─── Methodology Tab ───────────────────────────────────────────────────────────
function MethodologyTab() {
  return (
    <div style={SECTION_GAP}>

      <div style={CARD}>
        <h3 style={{ margin: '0 0 0.75rem', color: '#3B2F2F', fontSize: '1rem' }}>¿Qué es el Building Resource Passport (BRP)?</h3>
        <p style={{ margin: 0, color: '#4A4A4A', fontSize: '0.9rem', lineHeight: 1.6 }}>
          El <strong>Building Resource Passport</strong> es una metodología desarrollada por DGNB para documentar y evaluar la circularidad de los materiales de construcción a lo largo de todo el ciclo de vida del edificio (versión <strong>v1.2.1, 2020</strong>).
        </p>
        <p style={{ margin: '0.75rem 0 0', color: '#4A4A4A', fontSize: '0.9rem', lineHeight: 1.6 }}>
          El resultado principal es el <strong>Índice de Circularidad (CI)</strong>, una puntuación de <strong>0,00 a 1,00</strong> que refleja cuánto se aproxima el edificio al modelo de economía circular: materiales de origen secundario/renovable, diseño para el desmontaje y alta recuperación al fin de vida.
        </p>
      </div>

      <div style={CARD}>
        <h3 style={{ margin: '0 0 1rem', color: '#3B2F2F', fontSize: '1rem' }}>Fórmula del Índice de Circularidad</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', justifyContent: 'center', padding: '1rem', background: '#F5F0EA', borderRadius: '8px', marginBottom: '1rem', flexWrap: 'wrap' }}>
          {[
            { label: 'CI', sub: '0,00 – 1,00', color: '#8D9A2E', size: '2.5rem' },
            { label: '=', sub: '', color: '#7A6A5A', size: '1.5rem' },
            { label: 'CI(C)', sub: 'Fase previa al uso · 0,00 – 0,50', color: '#7A8FA0', size: '1.5rem' },
            { label: '+', sub: '', color: '#7A6A5A', size: '1.5rem' },
            { label: 'CI(F)', sub: 'Fase posterior al uso · 0,00 – 0,50', color: '#C98A2E', size: '1.5rem' },
          ].map((it, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: it.size, fontWeight: 700, color: it.color, lineHeight: 1 }}>{it.label}</div>
              {it.sub && <div style={{ fontSize: '0.72rem', color: '#7A6A5A', marginTop: '0.2rem' }}>{it.sub}</div>}
            </div>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div>
            <div style={{ fontWeight: 600, color: '#7A8FA0', marginBottom: '0.5rem', fontSize: '0.85rem' }}>CI(C) — Sub-indicadores pre-uso</div>
            {[
              { code: 'MOI', name: 'Material Origin Index', w: '55%', desc: '% de materiales secundarios (reciclado, reutilizado) o bio-renovables. La fuente más importante del CI.' },
              { code: 'PLI', name: 'Pollutant Load Index',  w: '20%', desc: 'Carga de sustancias peligrosas (REACH, CLP). A menor contaminación, mayor CI.' },
              { code: 'MCI', name: 'Material Compatibility',w: '10%', desc: 'Capacidad de los materiales para reciclarse juntos sin perder calidad.' },
              { code: 'CDW', name: 'C&D Waste Index',       w: '5%',  desc: 'Jerarquía de gestión de residuos de construcción (prevención > reutilización > reciclaje).' },
            ].map(s => (
              <div key={s.code} style={{ marginBottom: '0.5rem', padding: '0.5rem', background: '#F0F4FA', borderRadius: '6px' }}>
                <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                  <span style={{ fontWeight: 700, color: '#7A8FA0', fontSize: '0.75rem', minWidth: '2.8rem' }}>{s.code}</span>
                  <span style={{ fontWeight: 600, fontSize: '0.82rem', color: '#3B2F2F' }}>{s.name}</span>
                  <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#8D9A2E', fontWeight: 700 }}>{s.w}</span>
                </div>
                <div style={{ fontSize: '0.77rem', color: '#7A6A5A', marginTop: '0.2rem', paddingLeft: '3.2rem' }}>{s.desc}</div>
              </div>
            ))}
          </div>
          <div>
            <div style={{ fontWeight: 600, color: '#C98A2E', marginBottom: '0.5rem', fontSize: '0.85rem' }}>CI(F) — Sub-indicadores post-uso</div>
            {[
              { code: 'RPI', name: 'Recycling Potential Index', w: '60%', desc: 'Potencial de reciclaje de cada material al fin de vida (metales: alto; EPS: bajo).' },
              { code: 'DI',  name: 'Detachability Index',        w: '20%', desc: 'Facilidad de desmontaje según tipo de unión: Clase A (reversible) → Clase E (pegado rígido).' },
              { code: 'MSI', name: 'Material Separability',      w: '20%', desc: 'Grado de separabilidad de materiales y capas constructivas al final de su vida útil.' },
            ].map(s => (
              <div key={s.code} style={{ marginBottom: '0.5rem', padding: '0.5rem', background: '#FFF7EE', borderRadius: '6px' }}>
                <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                  <span style={{ fontWeight: 700, color: '#C98A2E', fontSize: '0.75rem', minWidth: '2.8rem' }}>{s.code}</span>
                  <span style={{ fontWeight: 600, fontSize: '0.82rem', color: '#3B2F2F' }}>{s.name}</span>
                  <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#8D9A2E', fontWeight: 700 }}>{s.w}</span>
                </div>
                <div style={{ fontSize: '0.77rem', color: '#7A6A5A', marginTop: '0.2rem', paddingLeft: '3.2rem' }}>{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={CARD}>
        <h3 style={{ margin: '0 0 0.75rem', color: '#3B2F2F', fontSize: '1rem' }}>Clases de Desmontabilidad (Detachability Index)</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.5rem' }}>
          {[
            { clase: 'A', score: '1,00', label: 'Reversible',  color: '#5A8A5A', examples: 'Perfiles metálicos atornillados, vidrio en marcos, paneles tipo sándwich con tornillos' },
            { clase: 'B', score: '0,75', label: 'Accesible',   color: '#8D9A2E', examples: 'Fijaciones mecánicas visibles, tarimas flotantes, falsos techos con ganchos' },
            { clase: 'C', score: '0,50', label: 'Rompible',    color: '#C98A2E', examples: 'Anclajes de expansión, remaches, grapas estructurales' },
            { clase: 'D', score: '0,25', label: 'Adhesiva',    color: '#D4884A', examples: 'Sellantes, espumas de poliuretano, adhesivos elásticos' },
            { clase: 'E', score: '0,00', label: 'Rígida',      color: '#D4614A', examples: 'Hormigón in situ, soldadura, pegado rígido con epóxi' },
          ].map(c => (
            <div key={c.clase} style={{ padding: '0.75rem 0.5rem', background: '#F5F0EA', borderRadius: '6px', border: `2px solid ${c.color}`, textAlign: 'center' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: c.color }}>{c.clase}</div>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: c.color }}>{c.score}</div>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#3B2F2F', marginTop: '0.25rem' }}>{c.label}</div>
              <div style={{ fontSize: '0.7rem', color: '#9A8A7A', marginTop: '0.3rem', fontStyle: 'italic', lineHeight: 1.3 }}>{c.examples}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={CARD}>
        <h3 style={{ margin: '0 0 0.75rem', color: '#3B2F2F', fontSize: '1rem' }}>Conexiones con otras normativas del proyecto</h3>
        <div style={SECTION_GAP}>
          {[
            {
              icon: null, title: 'BREEAM MAT — MAT 1, MAT 3, MAT 4',
              points: [
                'MAT 1 (Impacto del ciclo de vida): las EPDs exigidas contienen datos de masa y origen → alimentan directamente el inventario BRP',
                'MAT 3 (Materiales responsables): las certificaciones FSC, BES 6001, etc. acreditan el origen bio-renovable o reciclado → contribuyen al MOI',
                'MAT 4 (Aislamiento): el tipo de aislamiento afecta al PLI si contiene HBCD u otras sustancias restringidas por REACH',
                'Los documentos de mediciones (BOQ) subidos para análisis MAT son la fuente más directa del inventario de masas',
              ],
              note: 'Recomendación: completa el Inventario de Masas en el tab siguiente usando los datos de los análisis MAT ya realizados.',
              noteColor: '#8D9A2E',
            },
            {
              icon: null, title: 'Taxonomía Europea — Actividades 3.1 y 3.2',
              points: [
                'El criterio DNSH "Economía Circular" exige para actividades 3.1/3.2 evaluar los materiales conforme a EU Level(s) indicador 2.3',
                'Un CI alto es evidencia directa del cumplimiento del Objetivo 4 de Economía Circular de la Taxonomía',
                'La Taxonomía exige un Plan de Gestión de Residuos — el CDW score del BRP documenta este cumplimiento',
              ],
              note: 'Ver pestaña Taxonomía Europea para el análisis completo de las actividades 3.1/3.2.',
              noteColor: '#7A8FA0',
            },
            {
              icon: null, title: 'EU Level(s) — Indicador 2.3',
              points: [
                'Level(s) es el marco europeo de sostenibilidad para edificios; el indicador 2.3 mide el potencial de uso eficiente de materiales',
                'El CI calculado con el BRP puede reportarse como evidencia del indicador 2.3 de Level(s)',
                'Requerido como evidencia del criterio DNSH de Economía Circular de la Taxonomía para edificios nuevos',
              ],
              note: null, noteColor: null,
            },
          ].map((item, i) => (
            <div key={i} style={{ padding: '0.75rem 1rem', background: '#F5F0EA', borderRadius: '6px' }}>
              <div style={{ fontWeight: 600, color: '#3B2F2F', marginBottom: '0.4rem', fontSize: '0.9rem' }}>{item.title}</div>
              <ul style={{ margin: '0 0 0.4rem', paddingLeft: '1.2rem' }}>
                {item.points.map((p, j) => <li key={j} style={{ fontSize: '0.83rem', color: '#4A4A4A', marginBottom: '0.2rem' }}>{p}</li>)}
              </ul>
              {item.note && <div style={{ fontSize: '0.82rem', color: item.noteColor, fontWeight: 500, borderLeft: `3px solid ${item.noteColor}`, paddingLeft: '0.5rem', marginTop: '0.4rem' }}>{item.note}</div>}
            </div>
          ))}
        </div>
      </div>

      <div style={{ ...CARD, borderLeft: '4px solid #C98A2E' }}>
        <h3 style={{ margin: '0 0 0.75rem', color: '#C98A2E', fontSize: '1rem' }}>¿Qué necesita tu proyecto para calcular el CI?</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
          {[
            { item: 'Inventario de masas por categoría de material (en toneladas o %)', status: 'pendiente' },
            { item: 'Clasificación del origen de cada material (primario / reciclado / bio-renovable)', status: 'pendiente' },
            { item: 'Clases de desmontabilidad por elemento constructivo (A–E)', status: 'pendiente' },
            { item: 'Potencial de reciclaje estimado por tipo de material', status: 'pendiente' },
            { item: 'Estimación de gestión de residuos de construcción (CDW)', status: 'pendiente' },
            { item: 'Evaluación de sustancias peligrosas / contaminantes (PLI)', status: 'pendiente' },
            { item: 'Análisis BREEAM MAT realizados → base de datos de materiales ya disponible', status: 'disponible' },
            { item: 'Certificaciones FSC / BES 6001 para el MOI (si tienes MAT 3)', status: 'disponible' },
          ].map((it, i) => (
            <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', padding: '0.45rem 0.6rem', background: it.status === 'disponible' ? '#F0F7E8' : '#FFF7EE', borderRadius: '5px' }}>
              {it.status === 'disponible'
                ? <CheckCircle size={15} color="#5A8A5A" weight="fill" style={{ flexShrink: 0, marginTop: '1px' }} />
                : <Warning size={15} color="#C98A2E" style={{ flexShrink: 0, marginTop: '1px' }} />
              }
              <span style={{ fontSize: '0.82rem', color: '#4A4A4A', lineHeight: 1.4 }}>{it.item}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── Helper: call breeam-analyzer Edge Function (SSE streaming) ───────────────
async function callBreeamAnalyzerLocal(functionUrl, anonKey, body) {
  let response
  try {
    response = await fetch(functionUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${anonKey}`, 'apikey': anonKey },
      body: JSON.stringify(body),
    })
  } catch (netErr) {
    // TypeError / CORS block / network failure
    throw new Error(`No se pudo conectar con la Edge Function (${netErr.message}). Comprueba que el servidor de desarrollo está activo y que la función breeam-analyzer está desplegada en Supabase.`)
  }
  if (!response.ok) {
    let d = {}
    try { d = await response.json() } catch (_) {}
    if (response.status === 502 || response.status === 503 || response.status === 504 || response.status === 524) {
      throw new Error(`La Edge Function devolvió error ${response.status}. Esto suele ocurrir cuando la función necesita redeployarse o hay un problema temporal en Supabase. Prueba a re-analizar cualquier criterio BREEAM para verificar que la función funciona, o ve al panel de Supabase → Edge Functions → breeam-analyzer y redeploy.`)
    }
    throw new Error(`Error HTTP ${response.status}` + (d?.error ? `: ${d.error}` : ''))
  }
  const ct = response.headers.get('content-type') || ''
  if (ct.includes('text/event-stream')) {
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let text = '', buf = ''
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buf += decoder.decode(value, { stream: true })
      const lines = buf.split('\n'); buf = lines.pop() || ''
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const ds = line.slice(6).trim()
        if (!ds || ds === '[DONE]') continue
        try {
          const ev = JSON.parse(ds)
          if (ev.type === 'content_block_delta' && ev.delta?.type === 'text_delta') text += ev.delta.text
          else if (ev.error) throw new Error(ev.error.message || JSON.stringify(ev.error))
        } catch (e) { if (e.message?.startsWith('Error')) throw e }
      }
    }
    return { content: [{ type: 'text', text }] }
  }
  return await response.json()
}

// ─── Inventory Tab ─────────────────────────────────────────────────────────────
function InventoryTab({ rows, cdw, pli, onAdd, onUpdate, onDelete, onCdw, onPli,
                        importLoading, importError, importWarnings, importSuccess, onImport,
                        project }) {
  const [showPresets, setShowPresets] = useState(false)
  const [cdwOverride, setCdwOverride] = useState(false)

  // ── Auto-derive CDW from RSD 1 (Reducción de residuos de construcción, max 3 pts)
  const rsd1 = project?.evaluations?.['Residuos-RSD 1']
  const rsd1Pts = rsd1?.points_obtained ?? null
  const cdwAuto = rsd1Pts !== null ? Math.round((rsd1Pts / 3) * 100) : null

  // ── Hint for PLI from MAT 4 (Aislamiento, max 3 pts — higher score = better materials = lower pollutants)
  const mat4 = project?.evaluations?.['Materiales-MAT 4']
  const mat4Pts = mat4?.points_obtained ?? null
  const totalMasa = rows.reduce((s, r) => s + (parseFloat(r.masa_t) || 0), 0)

  const SEL = (field, options, row) => (
    <select value={row[field]} onChange={e => onUpdate(row.id, field, e.target.value)}
      style={{ fontSize: '0.78rem', border: '1px solid #DDD5C8', borderRadius: '4px', padding: '0.2rem 0.3rem', background: '#fff', color: '#3B2F2F', width: '100%' }}>
      {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  )

  return (
    <div style={SECTION_GAP}>
      {/* Top actions */}
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={() => onAdd()} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: '#4A4A4A', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.45rem 0.9rem', cursor: 'pointer', fontSize: '0.85rem' }}>
          <Plus size={15} /> Añadir material
        </button>
        <div style={{ position: 'relative' }}>
          <button onClick={() => setShowPresets(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: '#8D9A2E', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.45rem 0.9rem', cursor: 'pointer', fontSize: '0.85rem' }}>
            <Package size={15} /> Usar presets
          </button>
          {showPresets && (
            <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 100, background: '#fff', border: '1px solid #DDD5C8', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: '240px', padding: '0.5rem' }}>
              {PRESETS.map((p, i) => (
                <button key={i} onClick={() => { onAdd(p); setShowPresets(false) }}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.4rem 0.6rem', border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.83rem', color: '#3B2F2F', borderRadius: '4px' }}
                  onMouseEnter={e => e.target.style.background = '#F5F0EA'}
                  onMouseLeave={e => e.target.style.background = 'none'}>
                  {p.nombre}
                </button>
              ))}
            </div>
          )}
        </div>
        <button onClick={onImport} disabled={importLoading}
          style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: importLoading ? '#9A8A7A' : '#7A8FA0', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.45rem 0.9rem', cursor: importLoading ? 'default' : 'pointer', fontSize: '0.85rem' }}>
          <DownloadSimple size={15} />
          {importLoading ? 'Analizando documentos MAT…' : 'Importar desde MAT'}
        </button>
        {totalMasa > 0 && (
          <span style={{ marginLeft: 'auto', fontSize: '0.85rem', color: '#7A6A5A' }}>
            Total masa: <strong>{totalMasa.toLocaleString('es-ES', { maximumFractionDigits: 1 })} t</strong>
          </span>
        )}
      </div>
      {/* Import feedback */}
      {importError && (
        <div style={{ padding: '0.75rem 1rem', background: '#FEF0EE', border: '1px solid #F5C6BE', borderRadius: '6px', fontSize: '0.83rem', color: '#C0392B', display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
          <Warning size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
          <span><strong>Error al importar:</strong> {importError}</span>
        </div>
      )}
      {importSuccess && (
        <div style={{ padding: '0.75rem 1rem', background: '#F0F7E8', border: '1px solid #C3E0A8', borderRadius: '6px', fontSize: '0.83rem', color: '#3A7A2E' }}>
          <CheckCircle size={16} style={{ verticalAlign: 'middle', marginRight: '0.4rem' }} />
          {importSuccess}
          {importWarnings?.length > 0 && (
            <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.2rem' }}>
              {importWarnings.map((w, i) => <li key={i} style={{ fontSize: '0.8rem', color: '#7A6A5A' }}>{w}</li>)}
            </ul>
          )}
        </div>
      )}

      {/* Inventory table */}
      {rows.length === 0 ? (
        <div style={{ ...CARD, textAlign: 'center', padding: '3rem', color: '#9A8A7A' }}>
          <Package size={40} color="#DDD5C8" style={{ display: 'block', margin: '0 auto 0.75rem' }} />
          <div style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.4rem' }}>Sin materiales todavía</div>
          <div style={{ fontSize: '0.85rem' }}>Añade materiales manualmente o usa los presets para empezar el inventario</div>
        </div>
      ) : (
        <div style={{ ...CARD, padding: '0', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ background: '#F5F0EA' }}>
                {['Material', 'Masa (t)', 'Origen', 'Compatib.', 'Desmontab.', 'Separab.', 'Pot. Reciclaje', ''].map((h, i) => (
                  <th key={i} style={{ padding: '0.6rem 0.75rem', textAlign: 'left', color: '#7A6A5A', fontWeight: 600, borderBottom: '1px solid #DDD5C8', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={r.id} style={{ borderBottom: '1px solid #F0EBE3', background: idx % 2 === 0 ? '#fff' : '#FAFAF8' }}>
                  <td style={{ padding: '0.5rem 0.75rem' }}>
                    <input value={r.nombre} onChange={e => onUpdate(r.id, 'nombre', e.target.value)} placeholder="Nombre del material"
                      style={{ fontSize: '0.82rem', border: '1px solid #DDD5C8', borderRadius: '4px', padding: '0.25rem 0.4rem', width: '160px', color: '#3B2F2F' }} />
                  </td>
                  <td style={{ padding: '0.5rem 0.75rem' }}>
                    <input type="number" value={r.masa_t} onChange={e => onUpdate(r.id, 'masa_t', e.target.value)} placeholder="0"
                      style={{ fontSize: '0.82rem', border: '1px solid #DDD5C8', borderRadius: '4px', padding: '0.25rem 0.4rem', width: '70px', textAlign: 'right', color: '#3B2F2F' }} />
                  </td>
                  <td style={{ padding: '0.5rem 0.75rem', minWidth: '110px' }}>
                    {SEL('origen', [{ v: 'primario', l: 'Primario' }, { v: 'bio-renovable', l: 'Bio-renovable' }, { v: 'reciclado', l: 'Reciclado' }, { v: 'reutilizado', l: 'Reutilizado' }], r)}
                  </td>
                  <td style={{ padding: '0.5rem 0.75rem', minWidth: '90px' }}>
                    {SEL('compatibilidad', [{ v: 'alto', l: 'Alta' }, { v: 'medio', l: 'Media' }, { v: 'bajo', l: 'Baja' }], r)}
                  </td>
                  <td style={{ padding: '0.5rem 0.75rem', minWidth: '100px' }}>
                    {SEL('desmontabilidad', ['A','B','C','D','E'].map(v => ({ v, l: `Clase ${v}` })), r)}
                  </td>
                  <td style={{ padding: '0.5rem 0.75rem', minWidth: '90px' }}>
                    {SEL('separabilidad', [{ v: 'alto', l: 'Alta' }, { v: 'medio', l: 'Media' }, { v: 'bajo', l: 'Baja' }], r)}
                  </td>
                  <td style={{ padding: '0.5rem 0.75rem', minWidth: '90px' }}>
                    {SEL('potencial_reciclaje', [{ v: 'alto', l: 'Alto' }, { v: 'medio', l: 'Medio' }, { v: 'bajo', l: 'Bajo' }], r)}
                  </td>
                  <td style={{ padding: '0.5rem 0.75rem' }}>
                    <button onClick={() => onDelete(r.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#D4614A', padding: '0.2rem' }}>
                      <Trash size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* CDW and PLI */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>

        {/* CDW — auto from RSD 1 */}
        <div style={CARD}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
            <div style={{ fontWeight: 600, color: '#3B2F2F', fontSize: '0.9rem' }}>CDW — Gestión de Residuos</div>
            {cdwAuto !== null && (
              <span style={{ fontSize: '0.72rem', padding: '0.15rem 0.5rem', background: '#F0F7E8', border: '1px solid #C3E0A8', borderRadius: '10px', color: '#5A8A5A', fontWeight: 600 }}>
                Auto · RSD 1
              </span>
            )}
          </div>

          {cdwAuto !== null && !cdwOverride ? (
            <>
              <div style={{ fontSize: '0.82rem', color: '#7A6A5A', marginBottom: '0.75rem' }}>
                Calculado desde <strong>RSD 1</strong>: {rsd1Pts}/3 pts obtenidos
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                <div style={{ flex: 1, height: 8, background: '#EDE7DE', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${cdwAuto}%`, background: '#8D9A2E', borderRadius: 4 }} />
                </div>
                <span style={{ fontWeight: 700, color: '#8D9A2E', fontSize: '1rem', minWidth: '2.5rem', textAlign: 'right' }}>{cdwAuto}%</span>
              </div>
              <button onClick={() => { setCdwOverride(true); onCdw(cdwAuto) }}
                style={{ fontSize: '0.75rem', color: '#7A8FA0', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                Introducir manualmente
              </button>
            </>
          ) : (
            <>
              <div style={{ fontSize: '0.82rem', color: '#7A6A5A', marginBottom: '0.75rem' }}>
                {cdwAuto === null ? 'RSD 1 no analizado. Introduce el % manualmente.' : 'Valor manual (RSD 1 disponible)'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <input type="range" min={0} max={100} value={cdw} onChange={e => onCdw(Number(e.target.value))} style={{ flex: 1 }} />
                <input type="number" min={0} max={100} value={cdw} onChange={e => onCdw(Math.min(100, Math.max(0, Number(e.target.value))))}
                  style={{ width: '52px', textAlign: 'right', border: '1px solid #DDD5C8', borderRadius: '4px', padding: '0.2rem 0.4rem', fontSize: '0.85rem', fontWeight: 600, color: '#3B2F2F' }} />
                <span style={{ fontSize: '0.85rem', color: '#7A6A5A' }}>%</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#9A8A7A', marginTop: '0.25rem' }}>
                <span>Solo legal</span>
                {cdwAuto !== null && <button onClick={() => { setCdwOverride(false); onCdw(cdwAuto) }}
                  style={{ fontSize: '0.72rem', color: '#8D9A2E', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                  Usar valor RSD 1
                </button>}
                <span>Máx. jerarquía</span>
              </div>
            </>
          )}
        </div>

        {/* PLI — manual with MAT 4 hint */}
        <div style={CARD}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
            <div style={{ fontWeight: 600, color: '#3B2F2F', fontSize: '0.9rem' }}>PLI — Carga de Contaminantes</div>
            {mat4Pts !== null && (
              <span style={{ fontSize: '0.72rem', padding: '0.15rem 0.5rem', background: '#FFF7EE', border: '1px solid #EDDFCC', borderRadius: '10px', color: '#C98A2E', fontWeight: 600 }}>
                Ref. MAT 4
              </span>
            )}
          </div>
          <div style={{ fontSize: '0.82rem', color: '#7A6A5A', marginBottom: '0.75rem' }}>
            ¿Qué % de los materiales contienen sustancias preocupantes (SVHC REACH, HBCD, etc.)?
            {mat4Pts !== null && <span style={{ display: 'block', marginTop: '0.2rem', color: '#C98A2E' }}>MAT 4: {mat4Pts}/3 pts — a mayor puntuación, menor carga de contaminantes.</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <input type="range" min={0} max={100} value={pli} onChange={e => onPli(Number(e.target.value))} style={{ flex: 1 }} />
            <input type="number" min={0} max={100} value={pli} onChange={e => onPli(Math.min(100, Math.max(0, Number(e.target.value))))}
              style={{ width: '52px', textAlign: 'right', border: '1px solid #DDD5C8', borderRadius: '4px', padding: '0.2rem 0.4rem', fontSize: '0.85rem', fontWeight: 600, color: '#3B2F2F' }} />
            <span style={{ fontSize: '0.85rem', color: '#7A6A5A' }}>%</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#9A8A7A', marginTop: '0.25rem' }}>
            <span>Sin contaminantes</span><span>Alto contenido</span>
          </div>
        </div>

      </div>

      <div style={{ padding: '0.75rem 1rem', background: '#F0F4FA', borderRadius: '6px', fontSize: '0.82rem', color: '#4A4A4A', border: '1px solid #D0DCF0' }}>
        <strong>Importar desde MAT:</strong> si ya has subido documentos de mediciones o presupuesto a los criterios MAT 1, MAT 3 o MAT 4, el botón <em>Importar desde MAT</em> los analiza automáticamente con IA para extraer los materiales y sus cantidades (en toneladas) y rellenar la tabla. Revisa y ajusta los valores importados antes de calcular el CI.
      </div>
    </div>
  )
}

// ─── Result Tab ────────────────────────────────────────────────────────────────
function ResultTab({ ciData, rows }) {
  if (!ciData) {
    return (
      <div style={{ ...CARD, textAlign: 'center', padding: '3rem' }}>
        <Recycle size={48} color="#DDD5C8" style={{ display: 'block', margin: '0 auto 1rem' }} />
        <div style={{ fontSize: '1rem', fontWeight: 600, color: '#9A8A7A', marginBottom: '0.4rem' }}>Sin datos suficientes</div>
        <div style={{ fontSize: '0.87rem', color: '#B0A89A' }}>Ve al tab <strong>Inventario de Masas</strong> y añade al menos un material con masa &gt; 0.</div>
      </div>
    )
  }

  const { ci, ci_c, ci_f, MOI, DI, MSI, RPI, MCI, CDW, PLI, total } = ciData
  const lv = ciLevel(ci)

  return (
    <div style={SECTION_GAP}>
      {/* Main CI score */}
      <div style={{ ...CARD, textAlign: 'center' }}>
        <div style={{ fontSize: '0.85rem', color: '#7A6A5A', marginBottom: '0.5rem', fontWeight: 500 }}>Índice de Circularidad (CI)</div>
        <div style={{ fontSize: '4rem', fontWeight: 800, color: lv.color, lineHeight: 1 }}>{ci.toFixed(2)}</div>
        <div style={{ fontSize: '1.1rem', color: lv.color, fontWeight: 700, marginTop: '0.25rem' }}>{lv.label}</div>
        <div style={{ fontSize: '0.8rem', color: '#9A8A7A', marginTop: '0.2rem' }}>Escala 0,00 – 1,00 · Masa analizada: {total.toLocaleString('es-ES', { maximumFractionDigits: 1 })} t</div>
        <div style={{ height: 14, background: '#EDE7DE', borderRadius: 7, margin: '1rem auto 0', maxWidth: 400, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${ci * 100}%`, background: lv.color, borderRadius: 7, transition: 'width 0.4s' }} />
        </div>
        {/* Scale markers */}
        <div style={{ display: 'flex', justifyContent: 'space-between', maxWidth: 400, margin: '0.25rem auto 0', fontSize: '0.7rem', color: '#9A8A7A' }}>
          <span>0 Bajo</span><span>0,45 Básico</span><span>0,60 Bueno</span><span>0,75 Excelente</span><span>1,00</span>
        </div>
      </div>

      {/* CI(C) + CI(F) breakdown */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div style={CARD}>
          <div style={{ fontWeight: 600, color: '#7A8FA0', marginBottom: '0.75rem', fontSize: '0.9rem' }}>CI(C) — Fase previa al uso</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#7A8FA0', marginBottom: '0.75rem' }}>{ci_c.toFixed(2)} <span style={{ fontSize: '0.85rem', color: '#9A8A7A' }}>/ 0,50</span></div>
          <SubBar label={`Origen de materiales — MOI (55%)`} value={MOI} color="#7A8FA0" />
          <SubBar label={`Sin contaminantes — PLI (20%): ${Math.round((1-PLI)*100)}%`} value={1 - PLI} color="#8D9A2E" />
          <SubBar label={`Compatibilidad materiales — MCI (10%)`} value={MCI} color="#9A8FA0" />
          <SubBar label={`Gestión residuos obra — CDW (5%): ${Math.round(CDW*100)}%`} value={CDW} color="#B0A890" />
        </div>
        <div style={CARD}>
          <div style={{ fontWeight: 600, color: '#C98A2E', marginBottom: '0.75rem', fontSize: '0.9rem' }}>CI(F) — Fase posterior al uso</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#C98A2E', marginBottom: '0.75rem' }}>{ci_f.toFixed(2)} <span style={{ fontSize: '0.85rem', color: '#9A8A7A' }}>/ 0,50</span></div>
          <SubBar label="Recycling Potential (RPI)" value={RPI} color="#C98A2E" />
          <SubBar label="Detachability (DI)" value={DI} color="#D4614A" />
          <SubBar label="Material Separability (MSI)" value={MSI} color="#D4884A" />
        </div>
      </div>

      {/* Recommendations */}
      <div style={CARD}>
        <h3 style={{ margin: '0 0 0.75rem', color: '#3B2F2F', fontSize: '1rem' }}>Recomendaciones para mejorar el CI</h3>
        <div style={SECTION_GAP}>
          {[
            { condition: MOI < 0.4, title: 'Aumentar contenido reciclado (MOI)', desc: 'Especificar áridos reciclados en hormigón (hasta 30%), acero con contenido reciclado certificado (>50%), madera FSC/PEFC. El MOI es el sub-indicador de mayor peso (55% del CI(C)).' },
            { condition: RPI < 0.5, title: 'Mejorar el potencial de reciclaje (RPI)', desc: 'Evitar materiales compuestos de difícil separación. Usar plásticos de un solo polímero, morteros sin aditivos contaminantes. El RPI tiene el mayor peso en el CI(F) (60%).' },
            { condition: DI < 0.4,  title: 'Diseñar para el desmontaje (DI)', desc: 'Especificar uniones reversibles (clases A y B): fijaciones mecánicas, paneles atornillados, perfiles desmontables. Evitar soldaduras y hormigonados donde sea posible.' },
            { condition: MSI < 0.4, title: 'Mejorar la separabilidad de materiales (MSI)', desc: 'Separar claramente las capas constructivas (fachada ventilada vs muro portante, falsos techos registrables). Facilita la recuperación selectiva al final de la vida útil.' },
          ].filter(r => r.condition).map((rec, i) => (
            <div key={i} style={{ padding: '0.75rem', background: '#FFF7EE', borderRadius: '6px', border: '1px solid #EDDFCC' }}>
              <div style={{ fontWeight: 600, color: '#3B2F2F', fontSize: '0.88rem', marginBottom: '0.2rem' }}>{rec.title}</div>
              <div style={{ fontSize: '0.82rem', color: '#7A6A5A', lineHeight: 1.5 }}>{rec.desc}</div>
            </div>
          ))}
          {MOI >= 0.4 && RPI >= 0.5 && DI >= 0.4 && MSI >= 0.4 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem', background: '#F0F7E8', borderRadius: '6px', color: '#5A8A5A' }}>
              <CheckCircle size={20} weight="fill" />
              <span style={{ fontSize: '0.88rem', fontWeight: 600 }}>Los sub-indicadores principales están en rango aceptable. Continúa mejorando para alcanzar nivel Excelente (&gt;0,75).</span>
            </div>
          )}
        </div>
      </div>

      {/* Taxonomy alignment */}
      <div style={{ ...CARD, borderLeft: '4px solid #7A8FA0' }}>
        <h3 style={{ margin: '0 0 0.5rem', color: '#7A8FA0', fontSize: '0.95rem' }}>Alineación con Taxonomía Europea</h3>
        <div style={{ fontSize: '0.85rem', color: '#4A4A4A', lineHeight: 1.6 }}>
          {ci >= 0.45
            ? <><CheckCircle size={16} color="#5A8A5A" style={{ verticalAlign: 'middle', marginRight: '0.3rem' }} />CI ≥ 0,45 — Cumplimiento básico del Objetivo 4 (Economía Circular) de la Taxonomía UE para actividades 3.1/3.2. Documenta este valor como evidencia del criterio DNSH de Economía Circular.</>
            : <><Warning size={16} color="#C98A2E" style={{ verticalAlign: 'middle', marginRight: '0.3rem' }} />CI &lt; 0,45 — Mejora recomendada antes de declarar cumplimiento del Objetivo 4 de Economía Circular de la Taxonomía. Ver recomendaciones arriba.</>
          }
        </div>
      </div>
    </div>
  )
}

// ─── Main component ────────────────────────────────────────────────────────────
export default function EconomiaCircular({ project }) {
  const LS_KEY = `circular_data_${project?.id || 'default'}`
  const [activeTab, setActiveTab] = useState('metodologia')
  const [rows,  setRows]  = useState([])
  const [cdw,   setCdw]   = useState(50)   // C&D waste hierarchy score 0-100
  const [pli,   setPli]   = useState(20)   // Pollutant load 0-100
  const [saved,     setSaved]     = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [loadedAt,  setLoadedAt]  = useState(null)

  // Import states
  const [importLoading,  setImportLoading]  = useState(false)
  const [importError,    setImportError]    = useState(null)
  const [importWarnings, setImportWarnings] = useState([])
  const [importSuccess,  setImportSuccess]  = useState(null)

  // Carga: último snapshot de esg_snapshots (framework=circular_inventory). Fallback localStorage legacy.
  useEffect(() => {
    let cancel = false
    async function load() {
      if (!project?.id) return
      const { data, error } = await supabase
        .from('esg_snapshots')
        .select('source_data, created_at')
        .eq('project_id', project.id)
        .eq('framework', 'circular_inventory')
        .order('created_at', { ascending: false })
        .limit(1)
      if (cancel) return
      if (error) { console.warn('Carga circular_inventory:', error.message); return }
      const snap = data?.[0]?.source_data
      if (snap?.rows) {
        setRows(snap.rows); setCdw(snap.cdw ?? 50); setPli(snap.pli ?? 20)
        setLoadedAt(data[0].created_at)
        return
      }
      // Fallback legacy localStorage
      try {
        const stored = JSON.parse(localStorage.getItem(LS_KEY) || 'null')
        if (stored) { setRows(stored.rows || []); setCdw(stored.cdw ?? 50); setPli(stored.pli ?? 20) }
      } catch {}
    }
    load()
    return () => { cancel = true }
  }, [project?.id, LS_KEY])

  // Sync CDW from RSD 1 whenever project evaluations change
  useEffect(() => {
    const rsd1Pts = project?.evaluations?.['Residuos-RSD 1']?.points_obtained ?? null
    if (rsd1Pts !== null) setCdw(Math.round((rsd1Pts / 3) * 100))
  }, [project?.evaluations])

  const save = async () => {
    setSaveError(null)
    if (!project?.id) { setSaveError('Sin proyecto activo'); return }
    const { data: u } = await supabase.auth.getUser()
    const user_id = u?.user?.id
    if (!user_id) { setSaveError('No autenticado'); return }
    const ci = calcCI(rows, cdw, pli)
    const inserts = [
      {
        project_id: project.id, user_id,
        framework: 'circular_inventory',
        indicator_code: 'BRP_INVENTORY',
        indicator_name: 'Building Resource Passport — inventario',
        value_numeric: ci?.total ?? null,
        value_unit: 't',
        source_module: 'EconomiaCircular',
        source_data: { rows, cdw, pli },
      },
    ]
    if (ci) {
      inserts.push({
        project_id: project.id, user_id,
        framework: 'circular',
        indicator_code: 'DGNB_CI',
        indicator_name: 'Índice de Circularidad DGNB (CI)',
        value_numeric: ci.ci,
        value_unit: '',
        source_module: 'EconomiaCircular',
        source_data: { ci_c: ci.ci_c, ci_f: ci.ci_f, MOI: ci.MOI, DI: ci.DI, MSI: ci.MSI, RPI: ci.RPI, MCI: ci.MCI, CDW: ci.CDW, PLI: ci.PLI, total_t: ci.total },
      })
      inserts.push({
        project_id: project.id, user_id,
        framework: 'taxonomy',
        indicator_code: 'EU_TAX_4.4_CE',
        indicator_name: 'Alineación Objetivo 4 (Economía Circular) Taxonomía UE',
        value_numeric: ci.ci >= 0.45 ? 1 : 0,
        value_text: ci.ci >= 0.45 ? 'Cumplimiento básico (CI ≥ 0,45)' : 'No cumple umbral CI ≥ 0,45',
        value_unit: 'bool',
        source_module: 'EconomiaCircular',
        source_data: { ci: ci.ci, threshold: 0.45 },
      })
    }
    const { error } = await supabase.from('esg_snapshots').insert(inserts)
    if (error) { setSaveError(error.message); return }
    // También guardar en localStorage como backup local
    try { localStorage.setItem(LS_KEY, JSON.stringify({ rows, cdw, pli })) } catch {}
    setSaved(true); setLoadedAt(new Date().toISOString())
    setTimeout(() => setSaved(false), 2200)
  }

  const exportInventoryCSV = () => {
    if (!rows.length) return
    const data = rows.map(r => ({
      material: r.nombre, masa_t: r.masa_t,
      origen: r.origen, compatibilidad: r.compatibilidad,
      desmontabilidad: r.desmontabilidad, separabilidad: r.separabilidad,
      potencial_reciclaje: r.potencial_reciclaje,
    }))
    downloadCSV(data, `EconCircular_inventario_${(project?.name || 'proyecto').replace(/[^\w-]+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`)
  }

  const exportCIPDF = () => {
    const ci = calcCI(rows, cdw, pli)
    if (!ci) { alert('Añade al menos un material con masa > 0 antes de exportar.'); return }
    const sections = [
      {
        title: 'Resumen — Índice de Circularidad (CI)',
        cols: ['Métrica', 'Valor'],
        rows: [
          ['CI total (DGNB)', ci.ci.toFixed(3)],
          ['Nivel', ciLevel(ci.ci).label],
          ['Cumple Taxonomía UE obj. 4', ci.ci >= 0.45 ? 'Sí (CI ≥ 0,45)' : 'No'],
          ['Masa total inventario', `${ci.total.toFixed(1)} t`],
        ],
      },
      {
        title: 'Sub-indicadores',
        cols: ['Sub-indicador', 'Valor', 'Peso'],
        rows: [
          ['CI(C) — Pre-uso',                  ci.ci_c.toFixed(3), '0,50'],
          ['MOI — Origen materiales',          ci.MOI.toFixed(3),  '55% de CI(C)'],
          ['1-PLI — Sin contaminantes',        (1 - ci.PLI).toFixed(3), '20% de CI(C)'],
          ['MCI — Compatibilidad materiales',  ci.MCI.toFixed(3),  '10% de CI(C)'],
          ['CDW — Gestión residuos obra',      ci.CDW.toFixed(3),  '5% de CI(C)'],
          ['CI(F) — Post-uso',                 ci.ci_f.toFixed(3), '0,50'],
          ['RPI — Potencial de reciclaje',     ci.RPI.toFixed(3),  '60% de CI(F)'],
          ['DI — Desmontabilidad',             ci.DI.toFixed(3),   '20% de CI(F)'],
          ['MSI — Separabilidad de materiales',ci.MSI.toFixed(3),  '20% de CI(F)'],
        ],
      },
      {
        title: 'Inventario de masas',
        cols: ['Material', 'Masa (t)', 'Origen', 'Desmont.', 'Reciclaje'],
        rows: rows.filter(r => parseFloat(r.masa_t) > 0).map(r => [r.nombre, r.masa_t, r.origen, r.desmontabilidad, r.potencial_reciclaje]),
      },
    ]
    openPrintWindow({ title: 'Building Resource Passport — Índice de Circularidad', projectName: project?.name || 'Proyecto', sections })
  }

  const addRow    = (preset = null) => setRows(prev => [...prev, newRow(preset || {})])
  const updateRow = (id, field, value) => setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r))
  const deleteRow = (id) => setRows(prev => prev.filter(r => r.id !== id))

  const importFromMAT = async () => {
    if (!project?.id) { setImportError('No hay proyecto activo.'); return }
    setImportLoading(true); setImportError(null); setImportWarnings([]); setImportSuccess(null)
    try {
      // 1a. Try evidences from MAT 1/3/4 evaluations first
      const { data: evals } = await supabase
        .from('evaluations').select('*, evidences(*)')
        .eq('project_id', project.id).eq('category', 'Materiales')
        .in('criteria_code', ['MAT 1', 'MAT 3', 'MAT 4'])

      // evidences use field: file_path (not storage_path)
      const evalEvidences = (evals || []).flatMap(ev => ev.evidences || [])

      // 1b. Fallback: library documents (project_documents) — use storage_path
      let sourceLabel = 'evidencias MAT'
      let docsToUse = []

      if (evalEvidences.length > 0) {
        docsToUse = evalEvidences.slice(0, 4).map(ev => ({
          file_name: ev.file_name,
          path: ev.file_path,       // evidences table uses file_path
          bucket: ev.storage_bucket || 'evidences',
        }))
      } else {
        // No MAT evidences — try library
        const { data: libDocs } = await supabase
          .from('project_documents').select('*')
          .eq('project_id', project.id)
          .order('uploaded_at', { ascending: false })
          .limit(4)
        if (!libDocs?.length) throw new Error('No se encontraron documentos en las evaluaciones MAT ni en la Biblioteca. Sube mediciones, presupuesto o BOQ al proyecto primero.')
        docsToUse = libDocs.map(d => ({
          file_name: d.file_name,
          path: d.storage_path,     // project_documents table uses storage_path
          bucket: 'evidences',
        }))
        sourceLabel = 'Biblioteca de Documentos'
      }

      // 2. Create signed URLs for PDF/image files
      const file_infos = []
      for (const doc of docsToUse) {
        const ext = (doc.file_name || '').split('.').pop().toLowerCase()
        const media_type = ext === 'pdf' ? 'application/pdf'
          : ['jpg','jpeg'].includes(ext) ? 'image/jpeg'
          : ext === 'png' ? 'image/png' : null
        if (!media_type) continue
        const { data: sd } = await supabase.storage.from(doc.bucket).createSignedUrl(doc.path, 3600)
        if (sd?.signedUrl) file_infos.push({ name: doc.file_name, signed_url: sd.signedUrl, media_type })
      }
      if (!file_infos.length) throw new Error('No se encontraron documentos PDF ni imágenes. Los archivos Excel o Word no pueden analizarse directamente.')

      // 3. Call breeam-analyzer with mass extraction prompt
      const functionUrl = process.env.REACT_APP_SUPABASE_URL?.trim() + '/functions/v1/breeam-analyzer'
      const anonKey     = process.env.REACT_APP_SUPABASE_ANON_KEY?.trim()
      const prompt = `Analiza los documentos adjuntos (mediciones, presupuesto o especificaciones de un proyecto de construcción).

Extrae los materiales de construcción PRINCIPALES, agrupando por categoría (máximo 15 entradas). Agrupa partidas similares en un único material (ej: todo el hormigón estructural en una entrada, todo el acero en una entrada). Para cada categoría:
- nombre: categoría de material (ej: "Hormigón estructural", "Acero corrugado", "Madera estructural", "Cerramiento cerámico")
- masa_t: cantidad total en toneladas. Si no está en toneladas, convierte: hormigón 2,5 t/m³, acero 7,85 t/m³, madera 0,5 t/m³, ladrillo/cerámica 1,8 t/m³, vidrio 25 kg/m², mortero 1,8 t/m³, aluminio 2,7 t/m³
- origen: "primario" | "reciclado" | "bio-renovable" | "reutilizado"
- compatibilidad: "alto" | "medio" | "bajo"
- desmontabilidad: "A" (atornillado/reversible) | "B" (fijaciones accesibles) | "C" (rompible) | "D" (adhesivo) | "E" (hormigonado/rígido)
- separabilidad: "alto" | "medio" | "bajo"
- potencial_reciclaje: "alto" (metales, vidrio) | "medio" (hormigón, madera, yeso) | "bajo" (EPS, compuestos)

Responde ÚNICAMENTE con JSON válido, sin texto adicional:
{"materiales":[{"nombre":"...","masa_t":0.0,"origen":"primario","compatibilidad":"medio","desmontabilidad":"C","separabilidad":"medio","potencial_reciclaje":"medio"}],"total_masa_t":0.0,"advertencias":[]}`

      const apiData = await callBreeamAnalyzerLocal(functionUrl, anonKey, {
        file_infos, prompt_text: prompt, model: 'claude-sonnet-4-6', max_tokens: 8192,
      })

      const rawText = apiData?.content?.[0]?.text || ''
      const cleaned = rawText.replace(/```(?:json)?\n?/g, '').trim()
      const jsonMatch = cleaned.match(/\{[\s\S]*\}/)
      if (!jsonMatch) throw new Error('La IA no devolvió un JSON válido. Inténtalo de nuevo.')
      let result
      try {
        result = JSON.parse(jsonMatch[0])
      } catch (parseErr) {
        // Truncated JSON — try to salvage completed material entries
        const partial = jsonMatch[0]
        const materialMatches = [...partial.matchAll(/\{[^{}]*"nombre"\s*:\s*"[^"]+[^{}]*"masa_t"\s*:\s*[\d.]+[^{}]*\}/g)]
        if (materialMatches.length === 0) throw new Error(`La respuesta JSON fue truncada y no se pudo recuperar. Sube un documento más corto o con menos partidas.`)
        result = { materiales: materialMatches.map(m => { try { return JSON.parse(m[0]) } catch { return null } }).filter(Boolean), advertencias: ['Respuesta truncada — se recuperaron materiales parciales'] }
      }

      const extracted = (result.materiales || []).filter(m => m.nombre && parseFloat(m.masa_t) > 0)
      if (!extracted.length) throw new Error('No se encontraron materiales con cantidad en los documentos. Asegúrate de que los documentos contienen mediciones o presupuesto con unidades.')

      const importedRows = extracted.map(m => newRow({
        nombre:             m.nombre || '',
        masa_t:             String(Math.round((parseFloat(m.masa_t) || 0) * 10) / 10),
        origen:             ['primario','reciclado','bio-renovable','reutilizado'].includes(m.origen) ? m.origen : 'primario',
        compatibilidad:     ['alto','medio','bajo'].includes(m.compatibilidad) ? m.compatibilidad : 'medio',
        desmontabilidad:    ['A','B','C','D','E'].includes(m.desmontabilidad) ? m.desmontabilidad : 'C',
        separabilidad:      ['alto','medio','bajo'].includes(m.separabilidad) ? m.separabilidad : 'medio',
        potencial_reciclaje:['alto','medio','bajo'].includes(m.potencial_reciclaje) ? m.potencial_reciclaje : 'medio',
      }))

      setRows(prev => [...prev, ...importedRows])
      if (result.advertencias?.length) setImportWarnings(result.advertencias)
      setImportSuccess(`Se importaron ${importedRows.length} materiales desde ${sourceLabel}${result.total_masa_t ? ` (masa total estimada: ${result.total_masa_t.toLocaleString('es-ES')} t)` : ''}. Revisa y ajusta los valores.`)
      setActiveTab('inventario')

    } catch (err) {
      setImportError(err.message)
    } finally {
      setImportLoading(false)
    }
  }

  const ciData = calcCI(rows, cdw, pli)

  const TABS = [
    { id: 'metodologia', label: 'Metodología BRP' },
    { id: 'inventario',  label: 'Inventario de Masas' },
    { id: 'resultado',   label: 'Índice de Circularidad' },
  ]

  return (
    <div style={{ fontFamily: 'inherit' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.4rem', color: '#3B2F2F', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Recycle size={26} color="#8D9A2E" weight="duotone" />
            Economía Circular — Building Resource Passport
          </h2>
          <p style={{ margin: '0.3rem 0 0', color: '#7A6A5A', fontSize: '0.88rem' }}>
            Metodología DGNB v1.2.1 · Índice de Circularidad (CI) · Conexión con BREEAM MAT y Taxonomía Europea
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          {ciData && (
            <div style={{ padding: '0.4rem 0.8rem', background: '#F5F0EA', borderRadius: '6px', fontSize: '0.85rem', color: '#4A4A4A' }}>
              CI actual: <strong style={{ color: ciLevel(ciData.ci).color }}>{ciData.ci.toFixed(2)}</strong>
            </div>
          )}
          <button onClick={exportInventoryCSV} disabled={!rows.length} title="Exportar inventario a CSV"
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: '#FFFFFF', color: '#5C4A3A', border: '1px solid #DDD5C8', borderRadius: '6px', padding: '0.45rem 0.85rem', cursor: rows.length ? 'pointer' : 'not-allowed', opacity: rows.length ? 1 : 0.5, fontSize: '0.82rem' }}>
            <FileCsv size={15} /> CSV
          </button>
          <button onClick={exportCIPDF} disabled={!ciData} title="Exportar informe PDF del CI"
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: '#FFFFFF', color: '#5C4A3A', border: '1px solid #DDD5C8', borderRadius: '6px', padding: '0.45rem 0.85rem', cursor: ciData ? 'pointer' : 'not-allowed', opacity: ciData ? 1 : 0.5, fontSize: '0.82rem' }}>
            <FilePdf size={15} /> PDF
          </button>
          <button onClick={save} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: saved ? '#8D9A2E' : '#4A4A4A', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.5rem 1rem', cursor: 'pointer', fontSize: '0.85rem', transition: 'background 0.2s' }}>
            <FloppyDisk size={16} /> {saved ? '✓ Guardado' : 'Guardar'}
          </button>
        </div>
        {saveError && <div style={{ flexBasis: '100%', color: '#D4614A', fontSize: '0.78rem', marginTop: '0.25rem' }}>Error al guardar: {saveError}</div>}
        {loadedAt && !saved && <div style={{ flexBasis: '100%', color: '#7A6A5A', fontSize: '0.72rem', marginTop: '0.25rem' }}>Último guardado en BD: {new Date(loadedAt).toLocaleString('es-ES')}</div>}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #DDD5C8', marginBottom: '1.5rem' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{ padding: '0.6rem 1.2rem', border: 'none', background: 'none', cursor: 'pointer', color: activeTab === t.id ? '#8D9A2E' : '#7A6A5A', fontWeight: activeTab === t.id ? 700 : 400, borderBottom: activeTab === t.id ? '2px solid #8D9A2E' : '2px solid transparent', marginBottom: '-2px', fontSize: '0.88rem', whiteSpace: 'nowrap' }}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'metodologia' && <MethodologyTab />}
      {activeTab === 'inventario'  && <InventoryTab rows={rows} cdw={cdw} pli={pli} onAdd={addRow} onUpdate={updateRow} onDelete={deleteRow} onCdw={setCdw} onPli={setPli} importLoading={importLoading} importError={importError} importWarnings={importWarnings} importSuccess={importSuccess} onImport={importFromMAT} project={project} />}
      {activeTab === 'resultado'   && <ResultTab ciData={ciData} rows={rows} />}
    </div>
  )
}
